import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

export default function Autocomplete({
  value, onChange, onPick, field = "title", placeholder, icon, testId, className = "",
}) {
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const ref = useRef(null);
  const tmr = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const fetchSugs = (val) => {
    clearTimeout(tmr.current);
    if (!val || val.trim().length < 2) { setSugs([]); setOpen(false); return; }
    tmr.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/jobs/suggest", { params: { q: val, field } });
        setSugs(data || []);
        setOpen((data || []).length > 0);
        setHi(-1);
      } catch { setSugs([]); setOpen(false); }
    }, 180);
  };

  const change = (e) => { onChange(e.target.value); fetchSugs(e.target.value); };
  const pick = (s) => { onChange(s); setOpen(false); setSugs([]); onPick && onPick(s); };
  const onKey = (e) => {
    if (!open || !sugs.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, sugs.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && hi >= 0) { e.preventDefault(); pick(sugs[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      {icon}
      <Input
        data-testid={testId}
        value={value}
        onChange={change}
        onKeyDown={onKey}
        onFocus={() => value && value.trim().length >= 2 && sugs.length && setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        className={`pl-10 bg-white/5 border-white/10 h-12 ${className}`}
      />
      {open && sugs.length > 0 && (
        <ul
          data-testid={`${testId}-suggestions`}
          className="absolute z-50 mt-1 w-full glass-strong rounded-xl border border-white/10 overflow-hidden shadow-2xl max-h-64 overflow-y-auto"
        >
          {sugs.map((s, i) => (
            <li
              key={s}
              data-testid={`${testId}-suggestion`}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setHi(i)}
              className={`px-4 py-2.5 text-sm cursor-pointer ${i === hi ? "bg-brand-emerald/15 text-brand-emerald" : "hover:bg-white/5"}`}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
